import {
    Download,
    Eye,
    Edit,
    Mail,
    MapPin,
    Phone,
    Search,
    ChevronDown,
    X,
    User,
    Users,
    FileText,
    GraduationCap,
    BookOpen,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { api, Batch, Program, User as UserInt } from '../api';
import { useAuth } from '../hooks/useAuth';
import ModalPortal from './ModalPortal';

interface Trainee {
    trainee_id: string;
    gender?: string;
    date_of_birth?: string;
    aadhaar_last4?: string;
    highest_education?: string;
    address?: string;
    city?: string;
    state?: string;
    pincode?: string;
    registered_on: string;
    created_at: string;
    user: UserInt;
    batch_trainees?: any;
    _count?: {
        registrations: number;
        batch_trainees: number;
        certificates: number;
        placements: number;
    };
}

interface TraineeDetails extends Trainee {
    registrations?: Array<{
        registration_id: string;
        registration_date: string;
        consent_timestamp: string;
        mobilisation: {
            mobilisation_id: number;
            title: string;
            program: Program;
            status: string;
        };
    }>;
    batch_trainees?: Array<{
        batch: {
            mobilisation_id: number;
            batch_code: string;
            program: Program;
            status: string;
            start_date: string;
            end_date: string;
        };
    }>;
}

const TraineeManagement: React.FC = () => {
    const { user } = useAuth();
    const [trainees, setTrainees] = useState<Trainee[]>([]);
    const [loading, setLoading] = useState(true);
    const [, setError] = useState<string | null>(null);
    const [, setSuccess] = useState<string | null>(null);

    // raw typing state + debounced searchTerm
    const [typingSearch, setTypingSearch] = useState('');
    const [searchTerm, setSearchTerm] = useState('');


    // Filters & pagination
    const [genderFilter, setGenderFilter] = useState('');
    const [batchOptions, setBatchOptions] = useState<Batch[]>([]);
    const [batchOptionsLoading, setBatchOptionsLoading] = useState(false);
    const [batchQuery, setBatchQuery] = useState('');
    const [batchFilterId, setBatchFilterId] = useState<number | null>(null);
    const [showBatchDropdown, setShowBatchDropdown] = useState(false);
    const [pagination, setPagination] = useState({ total: 0, limit: 10, offset: 0 });

    // Modals & form state
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);

    const [viewTrainee, setViewTrainee] = useState<TraineeDetails | null>(null);
    const [selectedTrainee, setSelectedTrainee] = useState<TraineeDetails | null>(null);

    const [formData, setFormData] = useState({
        full_name: '',
        gender: '',
        date_of_birth: '',
        email: '',
        phone: '',
        aadhaar_last4: '',
        highest_education: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
    });

    const isSystmAdmin = user?.role === 'SYSTEM_ADMIN';
    const isMPOrCenterAdmin = user?.role === 'MOBILISATION_PARTNER_ADMIN';
    const isTrainer = user?.role === 'TRAINER';
    const isTrainee = user?.role === 'TRAINEE';
    const headerTitle = isTrainee ? 'My Profile' : 'Trainee Management';

    const selectedBatch = useMemo(
        () => batchOptions.find((batch) => batch.batch_id === batchFilterId) ?? null,
        [batchOptions, batchFilterId]
    );

    const filteredBatchOptions = useMemo(() => {
        const q = batchQuery.trim().toLowerCase();
        if (!q) return batchOptions;
        return batchOptions.filter((batch) => {
            const code = batch.batch_code?.toLowerCase() ?? '';
            const programName = batch.program?.name?.toLowerCase() ?? '';
            const mobilisationName = batch.mobilisation_name?.toLowerCase() ?? '';
            return (
                code.includes(q) ||
                programName.includes(q) ||
                mobilisationName.includes(q)
            );
        });
    }, [batchOptions, batchQuery]);

    // debounce effect: wait 500ms after typing stops
    useEffect(() => {
        const handler = setTimeout(() => {
            setSearchTerm(typingSearch);
            // reset pagination to page 1 whenever search changes
            setPagination(p => ({ ...p, offset: 0 }));
        }, 500);
        return () => clearTimeout(handler);
    }, [typingSearch]);

    useEffect(() => {
        let isActive = true;
        if (isTrainee || !user) return;

        const loadBatchOptions = async () => {
            setBatchOptionsLoading(true);
            try {
                const pageSize = 200;
                let page = 1;
                let totalPages = 1;
                const all: Batch[] = [];
                while (page <= totalPages) {
                    const params: Record<string, string | number> = { page, pageSize };
                    if (user.role) params.userRole = user.role;
                    if (user.mp_id) params.mpId = user.mp_id;

                    const resp = await api.get(`/batches/api/batches`, { params });
                    if (!resp.data?.success) break;
                    if (Array.isArray(resp.data.data)) {
                        all.push(...resp.data.data);
                    }
                    totalPages = resp.data.pagination?.totalPages ?? 1;
                    page += 1;
                }

                if (isActive) {
                    setBatchOptions(all);
                }
            } catch (error) {
                console.error('Failed to load batch options', error);
            } finally {
                if (isActive) setBatchOptionsLoading(false);
            }
        };

        loadBatchOptions();

        return () => {
            isActive = false;
        };
    }, [isTrainee, user]);

    // fetch on debounced search + other filters + pagination.offset
    useEffect(() => {
        fetchTrainees();
    }, [
        searchTerm,
        genderFilter,
        batchFilterId,
        pagination.offset,
        user,
    ]);

    async function fetchTrainees() {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                limit: pagination.limit.toString(),
                offset: pagination.offset.toString(),
            });
            if (searchTerm) params.append('search', searchTerm);
            if (genderFilter) params.append('gender', genderFilter);
            if (batchFilterId) params.append('batchId', batchFilterId.toString());

            const resp = await api.get(`/trainees?${params.toString()}`);
            if (resp.data.success) {
                setTrainees(resp.data.data);
                setPagination((p) => ({ ...p, total: resp.data.total }));
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }

    function handleBatchSelect(batch: Batch | null) {
        setBatchFilterId(batch ? batch.batch_id : null);
        setBatchQuery(batch ? batch.batch_code : '');
        setShowBatchDropdown(false);
        setPagination(p => ({ ...p, offset: 0 }));
    }

    // Fetch detail for View/Edit
    async function fetchDetails(id: string): Promise<TraineeDetails | null> {
        try {
            const resp = await api.get(`/trainees/${id}`);
            return resp.data.success ? resp.data.data : null;
        } catch {
            return null;
        }
    }

    // CSV Export — pull the full filtered set, not just the visible page.
    async function exportCsv() {
        const headers = [
            'Name',
            'Gender',
            'DOB',
            'Email',
            'Phone',
            'Education',
            'City',
            'State',
        ];
        let allTrainees: Trainee[] = trainees;
        try {
            const params = new URLSearchParams({ page: '1', limit: '10000' });
            if (searchTerm) params.append('search', searchTerm);
            if (genderFilter) params.append('gender', genderFilter);
            if (batchFilterId) params.append('batchId', batchFilterId.toString());
            const resp = await api.get(`/trainees?${params.toString()}`);
            if (resp.data?.success && Array.isArray(resp.data.data)) {
                allTrainees = resp.data.data as Trainee[];
            }
        } catch (e) {
            console.error('CSV export fetch failed, falling back to current page', e);
        }

        const escape = (v: string) => {
            const needsQuote = /[",\n]/.test(v);
            const escaped = v.replace(/"/g, '""');
            return needsQuote ? `"${escaped}"` : escaped;
        };
        const rows = allTrainees.map((t) => [
            t.user.full_name,
            t.gender || '',
            t.date_of_birth?.split('T')[0] || '',
            t.user.email,
            t.user.phone,
            t.highest_education || '',
            t.city || '',
            t.state || '',
        ].map((v) => escape(String(v ?? ''))));
        const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'trainees.csv';
        a.click();
        URL.revokeObjectURL(url);
    }


    // View modal
    async function handleView(t: Trainee) {
        const det = await fetchDetails(t.trainee_id);
        if (det) {
            setViewTrainee(det);
            setShowViewModal(true);
        }
    }

    // Edit modal
    async function openEditModal(t: TraineeDetails) {
        setSelectedTrainee(t);
        setFormData({
            full_name: t.user.full_name,
            gender: t.gender || '',
            date_of_birth: t.date_of_birth?.split('T')[0] || '',
            email: t.user.email,
            phone: t.user.phone,
            aadhaar_last4: t.aadhaar_last4 || '',
            highest_education: t.highest_education || '',
            address: t.address || '',
            city: t.city || '',
            state: t.state || '',
            pincode: t.pincode || '',
        });
        setShowEditModal(true);
    }

    async function handleEdit(e: React.FormEvent) {
        e.preventDefault();
        if (!selectedTrainee) return;
        try {
            const trimmed = {
                ...formData,
                full_name: formData.full_name.trim(),
                email: formData.email.trim(),
                phone: formData.phone.trim(),
                address: formData.address.trim(),
                city: formData.city.trim(),
                state: formData.state.trim(),
                pincode: formData.pincode.trim(),
            };
            const { data } = await api.put(
                `/trainees/${selectedTrainee.trainee_id}`,
                trimmed
            );
            if (data.success) {
                setShowEditModal(false);
                fetchTrainees();
            }
        } catch (err: any) {
            alert(err.response?.data?.message || 'Edit error');
        }
    }

    if (loading)
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
            </div>
        );

    return (
        // className="space-y-6"
        <div >
            {/* Header */}
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gray-900">{headerTitle}</h1>
                {!isTrainee && <div className="flex gap-2">
                    <button
                        onClick={exportCsv}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
                    >
                        <Download className="w-4 h-4" /> Export CSV
                    </button>
                </div>}
            </div>

            {/* Filters */}
            {!isTrainee && (
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                        <div className="md:col-span-2 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Search trainees..."
                                value={typingSearch}
                                onChange={e => setTypingSearch(e.target.value)}
                                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                        </div>
                        <select
                            value={genderFilter}
                            onChange={e => setGenderFilter(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                            <option value="">All Genders</option>
                            <option value="MALE">Male</option>
                            <option value="FEMALE">Female</option>
                            <option value="OTHER">Other</option>
                        </select>
                        <div className="md:col-span-2 relative">
                            <input
                                type="text"
                                placeholder={batchOptionsLoading ? "Loading batches..." : "Filter by batch..."}
                                value={batchQuery}
                                onChange={(e) => setBatchQuery(e.target.value)}
                                onFocus={() => setShowBatchDropdown(true)}
                                onBlur={() => {
                                    setShowBatchDropdown(false);
                                    setBatchQuery(selectedBatch?.batch_code ?? '');
                                }}
                                className="px-3 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent pr-10"
                            />
                            {batchFilterId && (
                                <button
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleBatchSelect(null)}
                                    className="absolute right-8 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                            {showBatchDropdown && (
                                <div className="absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg max-h-64 overflow-auto">
                                    <button
                                        type="button"
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => handleBatchSelect(null)}
                                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                    >
                                        All Batches
                                    </button>
                                    {batchOptionsLoading && (
                                        <div className="px-3 py-2 text-sm text-gray-500">Loading batches...</div>
                                    )}
                                    {!batchOptionsLoading && filteredBatchOptions.length === 0 && (
                                        <div className="px-3 py-2 text-sm text-gray-500">No batches found.</div>
                                    )}
                                    {filteredBatchOptions.map((batch) => {
                                        const isSelected = batchFilterId === batch.batch_id;
                                        return (
                                            <button
                                                key={batch.batch_id}
                                                type="button"
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => handleBatchSelect(batch)}
                                                className={`w-full text-left px-3 py-2 hover:bg-gray-50 ${isSelected ? 'bg-gray-100' : ''}`}
                                            >
                                                <div className="text-sm font-medium text-gray-900">
                                                    {batch.batch_code}
                                                </div>
                                                {(batch.program?.name || batch.mobilisation_name) && (
                                                    <div className="text-xs text-gray-500">
                                                        {batch.program?.name || batch.mobilisation_name}
                                                    </div>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Statistics */}
            {/* <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center">
                        <Users className="w-8 h-8 text-blue-600" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Total Trainees</p>
                            <p className="text-2xl font-bold text-gray-900">{pagination.total}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center">
                        <BookOpen className="w-8 h-8 text-green-600" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">In Training</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {trainees.filter(t => (t._count?.batch_trainees || 0) > 0).length}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center">
                        <GraduationCap className="w-8 h-8 text-purple-600" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Certified</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {trainees.filter(t => (t._count?.certificates || 0) > 0).length}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center">
                        <FileText className="w-8 h-8 text-orange-600" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Placed</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {trainees.filter(t => (t._count?.placements || 0) > 0).length}
                            </p>
                        </div>
                    </div>
                </div>
            </div> */}

            {/* Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Trainee
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Contact
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                <div className="flex flex-col">
                                    <span>Status</span>
                                    <span className="mt-1 text-[10px] font-normal text-gray-400">
                                        Enrolled · Registered
                                    </span>
                                </div>
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Batches
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {trainees.map(t => (
                            <tr key={t.trainee_id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap flex items-center gap-3">
                                    <User className="w-6 h-6 text-gray-600" />
                                    <div>
                                        <div className="text-sm font-medium text-gray-900">
                                            {t.user.full_name}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            {t.gender || '–'}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                        <Mail className="w-4 h-4 text-gray-400" /> {t.user.email}
                                    </div>
                                    <div className="flex items-center gap-2 mt-1">
                                        <Phone className="w-4 h-4 text-gray-400" /> {t.user.phone}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {t.batch_trainees[0] ? 'Enrolled' : 'Registered'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {t.batch_trainees && t.batch_trainees.length > 0
                                        ? t.batch_trainees
                                            .map((bt: any) => bt.batch.batch_code)
                                            .join(', ')
                                        : 'N/A'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium flex gap-2">
                                    <button
                                        onClick={() => handleView(t)}
                                        className="text-indigo-600 hover:text-indigo-900"
                                    >
                                        <Eye className="w-5 h-5" />
                                    </button>
                                    {!isTrainee && <button
                                        onClick={() => openEditModal(t as TraineeDetails)}
                                        className="text-blue-600 hover:text-blue-900"
                                    >
                                        <Edit className="w-5 h-5" />
                                    </button>
                                    }
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {!isTrainee && (
                <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-700">
                        Showing {pagination.offset + 1} to{' '}
                        {Math.min(pagination.offset + pagination.limit, pagination.total)} of{' '}
                        {pagination.total} trainees
                    </span>
                    <div className="flex gap-2">
                        <button
                            onClick={() =>
                                setPagination(p => ({ ...p, offset: Math.max(0, p.offset - p.limit) }))
                            }
                            disabled={pagination.offset === 0}
                            className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50"
                        >
                            Previous
                        </button>
                        <button
                            onClick={() =>
                                setPagination(p => ({ ...p, offset: p.offset + p.limit }))
                            }
                            disabled={pagination.offset + pagination.limit >= pagination.total}
                            className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            {/* View Modal */}
            {showViewModal && viewTrainee && (
                <ModalPortal>
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200">
                            <h2 className="text-xl font-semibold text-gray-900">
                                Trainee Details
                            </h2>
                        </div>
                        <div className="p-6 space-y-6">
                            {/* Personal Info */}
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4">
                                    Personal Information
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-500">
                                            Full Name
                                        </label>
                                        <p className="text-sm text-gray-900">
                                            {viewTrainee.user.full_name}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-500">
                                            Gender
                                        </label>
                                        <p className="text-sm text-gray-900">
                                            {viewTrainee.gender || '–'}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-500">
                                            Date of Birth
                                        </label>
                                        <p className="text-sm text-gray-900">
                                            {viewTrainee.date_of_birth
                                                ? new Date(
                                                    viewTrainee.date_of_birth
                                                ).toLocaleDateString()
                                                : '–'}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-500">
                                            Education
                                        </label>
                                        <p className="text-sm text-gray-900">
                                            {viewTrainee.highest_education || '–'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            {/* Contact */}
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4">
                                    Contact Information
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-500">
                                            Email
                                        </label>
                                        <p className="text-sm text-gray-900">
                                            {viewTrainee.user.email}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-500">
                                            Phone
                                        </label>
                                        <p className="text-sm text-gray-900">
                                            {viewTrainee.user.phone}
                                        </p>
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-500">
                                            Address
                                        </label>
                                        <p className="text-sm text-gray-900">
                                            {`${viewTrainee.address}, ${viewTrainee.city}, ${viewTrainee.state} - ${viewTrainee.pincode}`}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            {/* Batches */}
                            {viewTrainee.batch_trainees && viewTrainee.batch_trainees.length > 0 && (
                                <div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                                        Training Batches
                                    </h3>
                                    <div className="space-y-2">
                                        {viewTrainee.batch_trainees.map((b) => {
                                            // find the registration that matches this mobilisation
                                            const reg = viewTrainee.registrations?.find(
                                                (r) => r.mobilisation.mobilisation_id === b.batch.mobilisation_id
                                            );
                                            return (
                                                <div
                                                    key={b.batch.batch_code}
                                                    className="bg-gray-50 p-4 rounded-lg"
                                                >
                                                    <div className="flex justify-between items-start">
                                                        <div>
                                                            <h4 className="font-medium text-gray-900">
                                                                {b.batch.batch_code}
                                                            </h4>
                                                            <p className="text-sm text-gray-600">
                                                                {b.batch.program.program_code} : {b.batch.program.name}
                                                            </p>
                                                            <p className="text-sm text-gray-600">
                                                                Consented:&nbsp;
                                                                {reg?.consent_timestamp
                                                                    ? new Date(reg.consent_timestamp).toLocaleDateString()
                                                                    : 'N/A'}
                                                            </p>
                                                        </div>
                                                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                                            {b.batch.status}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            <span className='text-small'>If your data is incorrect, please connect with Bajaj Team on xxxx@bajajfinserv.in</span>
                            {/* Registrations */}
                            {viewTrainee.registrations && viewTrainee.registrations?.length > 0 && (
                                <div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                                        Mobilisation Registrations
                                    </h3>
                                    <div className="space-y-2">
                                        {viewTrainee.registrations.map((r) => (
                                            <div
                                                key={r.registration_id}
                                                className="bg-gray-50 p-4 rounded-lg"
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <h4 className="font-medium text-gray-900">
                                                            {r.mobilisation.title}
                                                        </h4>
                                                        <p className="text-sm text-gray-600">
                                                            {`Registered on : ${new Date(r.registration_date).toLocaleDateString()}`}
                                                        </p>
                                                        <p className="text-sm text-gray-600">
                                                            {r.mobilisation.program.program_code} : {r.mobilisation.program.name}
                                                        </p>
                                                    </div>
                                                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                                        {r.mobilisation.status}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                        </div>
                        <div className="p-6 border-t border-gray-200 flex justify-end">
                            <button
                                onClick={() => setShowViewModal(false)}
                                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                            >
                                Close
                            </button>
                        </div>
                        </div>
                    </div>
                </ModalPortal>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedTrainee && (
                <ModalPortal>
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200">
                            <h2 className="text-xl font-semibold text-gray-900">
                                Edit Trainee
                            </h2>
                        </div>
                        <form
                            onSubmit={handleEdit}
                            onChangeCapture={() => {
                                setError(null);
                                setSuccess(null);
                            }}
                            className="p-6 space-y-4"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Full Name
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.full_name}
                                        onChange={(e) => {
                                            setFormData({ ...formData, full_name: e.target.value });
                                            setError(null);
                                            setSuccess(null);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Gender
                                    </label>
                                    <select
                                        value={formData.gender}
                                        onChange={(e) => {
                                            setFormData({ ...formData, gender: e.target.value });
                                            setError(null);
                                            setSuccess(null);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    >
                                        <option value="">Select Gender</option>
                                        <option value="MALE">Male</option>
                                        <option value="FEMALE">Female</option>
                                        <option value="OTHER">Other</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Date of Birth
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.date_of_birth}
                                        onChange={(e) => {
                                            setFormData({ ...formData, date_of_birth: e.target.value });
                                            setError(null);
                                            setSuccess(null);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => {
                                            setFormData({ ...formData, email: e.target.value });
                                            setError(null);
                                            setSuccess(null);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Phone
                                    </label>
                                    <input
                                        type="tel"
                                        value={formData.phone}
                                        onChange={(e) => {
                                            setFormData({ ...formData, phone: e.target.value });
                                            setError(null);
                                            setSuccess(null);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Aadhaar Last4
                                    </label>
                                    <input
                                        type="text"
                                        maxLength={4}
                                        value={formData.aadhaar_last4}
                                        onChange={(e) => {
                                            setFormData({ ...formData, aadhaar_last4: e.target.value });
                                            setError(null);
                                            setSuccess(null);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Highest Education
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.highest_education}
                                        onChange={(e) => {
                                            setFormData({ ...formData, highest_education: e.target.value });
                                            setError(null);
                                            setSuccess(null);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        City
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.city}
                                        onChange={(e) => {
                                            setFormData({ ...formData, city: e.target.value });
                                            setError(null);
                                            setSuccess(null);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        State
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.state}
                                        onChange={(e) => {
                                            setFormData({ ...formData, state: e.target.value });
                                            setError(null);
                                            setSuccess(null);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Pincode
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.pincode}
                                        onChange={(e) => {
                                            setFormData({ ...formData, pincode: e.target.value });
                                            setError(null);
                                            setSuccess(null);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Address
                                    </label>
                                    <textarea
                                        rows={3}
                                        value={formData.address}
                                        onChange={(e) => {
                                            setFormData({ ...formData, address: e.target.value });
                                            setError(null);
                                            setSuccess(null);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    />
                                </div>
                            </div>
                            <div className="pt-4 border-t border-gray-200 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowEditModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                                >
                                    Save Changes
                                </button>
                            </div>
                        </form>
                        </div>
                    </div>
                </ModalPortal>
            )}

        </div>
    );
};

export default TraineeManagement;
